import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar,
} from 'recharts';
import { salesUrl, salesSummaryUrl, saleByIdUrl, branchesUrl, usersUrl } from '../../BaseURL';
import Modal from '../../components/Modal';
import Receipt from '../../components/Receipt';
import { useFacility } from '../../context/FacilityContext';
import { fmtMoney, fmtNumber, fmtDate, titleCase } from '../../utils/format';
import { toastSuccess, showAxiosError } from '../../utils/swal';

// Yesterday, today, last 7 days, last 30 days — the presets everyone reaches for.
const isoDay = (d) => d.toISOString().slice(0, 10);
const RANGE_PRESETS = [
  { key: 'today',  label: 'Today',    days: 0  },
  { key: 'yday',   label: 'Yesterday', days: 1, single: true },
  { key: '7d',     label: 'Last 7 days',  days: 7  },
  { key: '30d',    label: 'Last 30 days', days: 30 },
  { key: 'mtd',    label: 'This month',   mtd: true },
];

const rangeFor = (preset) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (preset === 'today')  return { from: isoDay(today), to: isoDay(today) };
  if (preset === 'yday') {
    const y = new Date(today); y.setDate(y.getDate() - 1);
    return { from: isoDay(y), to: isoDay(y) };
  }
  if (preset === '7d') {
    const f = new Date(today); f.setDate(f.getDate() - 6);
    return { from: isoDay(f), to: isoDay(today) };
  }
  if (preset === '30d') {
    const f = new Date(today); f.setDate(f.getDate() - 29);
    return { from: isoDay(f), to: isoDay(today) };
  }
  if (preset === 'mtd') {
    const f = new Date(today.getFullYear(), today.getMonth(), 1);
    return { from: isoDay(f), to: isoDay(today) };
  }
  return { from: isoDay(today), to: isoDay(today) };
};

const METHOD_LABEL = {
  cash:     '💵 Cash',
  momo:     '📱 Mobile Money',
  card:     '💳 Card',
  bank:     '🏦 Bank transfer',
  credit:   '📝 On credit',
};

const Sales = () => {
  const { facility } = useFacility();
  const [me] = useState(() => { try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch (_) { return null; } });
  const isSuper   = me?.role === 'super_admin';
  const isManager = me?.role === 'branch_manager' || isSuper;
  const isCashier = me?.role === 'cashier';

  // Filters
  const [rangeKey, setRangeKey]     = useState('today');
  const [range, setRange]           = useState(rangeFor('today'));
  const [branchId, setBranchId]     = useState('');   // super admin only — empty = all branches
  const [cashierId, setCashierId]   = useState('');   // filter by specific cashier
  const [methodKey, setMethodKey]   = useState('');
  const [status,    setStatus]      = useState('');
  const [search,    setSearch]      = useState('');

  // Data
  const [rows, setRows]             = useState([]);
  const [summary, setSummary]       = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);

  // Lookups (branches for super admin, users for cashier filter dropdown)
  const [branches, setBranches]     = useState([]);
  const [users, setUsers]           = useState([]);

  // Sale detail modal
  const [view, setView]             = useState(null);

  // Fetch the "who works at this branch" list for the cashier filter.
  // Cashiers don't need this — they only see their own sales anyway.
  useEffect(() => {
    if (isCashier) return;
    axios.get(usersUrl).then((res) => {
      if (res.data?.success) setUsers(res.data.results || []);
    }).catch(() => {});
  }, [isCashier]);

  // Super admin: fetch every branch so we can render the branch picker chips.
  useEffect(() => {
    if (!isSuper) return;
    axios.get(branchesUrl).then((res) => {
      if (res.data?.success) setBranches(res.data.results || []);
    }).catch(() => {});
  }, [isSuper]);

  // The eligible cashier list for the dropdown: users at this branch,
  // filtered to roles that can actually make sales.
  const cashierOptions = useMemo(
    () => users.filter((u) =>
      u.is_active && ['cashier', 'branch_manager', 'super_admin', 'store_officer'].includes(u.role)
    ),
    [users]
  );

  const params = useMemo(() => {
    const p = {};
    if (range.from)          p.from = range.from;
    if (range.to)            p.to   = range.to;
    if (isSuper && branchId) p.branch_id = branchId;
    if (cashierId)           p.cashier_user_id = cashierId;
    if (methodKey)           p.payment_method  = methodKey;
    if (status)              p.status = status;
    return p;
  }, [range, isSuper, branchId, cashierId, methodKey, status]);

  const load = async () => {
    setLoading(true); setError(null);
    // Fire both requests but handle each independently. One request failing
    // shouldn't blank the whole page.
    const [rResult, sResult] = await Promise.allSettled([
      axios.get(salesUrl,        { params: { ...params, limit: 500 } }),
      axios.get(salesSummaryUrl, { params }),
    ]);
    const errs = [];
    if (rResult.status === 'fulfilled') {
      setRows(rResult.value.data?.results || []);
    } else {
      setRows([]);
      const m = rResult.reason?.response?.data?.message || rResult.reason?.message;
      errs.push(`Sales list: ${m}`);
    }
    if (sResult.status === 'fulfilled') {
      setSummary(sResult.value.data || null);
    } else {
      setSummary(null);
      const m = sResult.reason?.response?.data?.message || sResult.reason?.message;
      errs.push(`Summary: ${m}`);
    }
    if (errs.length) setError(errs.join(' · '));
    setLoading(false);
  };

  useEffect(() => { load().catch(() => {}); /* eslint-disable-next-line */ }, [JSON.stringify(params)]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((s) =>
      (s.receipt_no || '').toLowerCase().includes(q)
      || (s.cashier_name || '').toLowerCase().includes(q)
      || (s.customer_name || '').toLowerCase().includes(q)
    );
  }, [rows, search]);

  const openSale = async (id) => {
    try {
      const res = await axios.get(saleByIdUrl(id));
      if (res.data?.success) {
        setView({
          sale: res.data.sale, items: res.data.items, payments: res.data.payments,
          branchName: res.data.sale.branch_name, cashier: res.data.sale.cashier_name,
        });
      }
    } catch (err) { showAxiosError(err); }
  };

  // ─── CSV export ──────────────────────────────────────────────
  const exportCsv = () => {
    if (filteredRows.length === 0) { toastSuccess('Nothing to export'); return; }
    const scope = cashierId
      ? users.find((u) => u.id === Number(cashierId))?.full_name || 'user'
      : (isCashier ? me?.full_name : 'all');
    const header = ['Receipt No', 'Date', 'Cashier', 'Customer', 'Subtotal', 'Discount', 'Tax', 'Total', 'Paid', 'Status'];
    const lines = [header.join(',')];
    for (const s of filteredRows) {
      lines.push([
        s.receipt_no,
        new Date(s.created_at).toISOString(),
        `"${(s.cashier_name || '').replace(/"/g, '""')}"`,
        `"${(s.customer_name || 'Walk-in').replace(/"/g, '""')}"`,
        Number(s.subtotal || 0).toFixed(2),
        Number(s.discount_amount || 0).toFixed(2),
        Number(s.tax_amount || 0).toFixed(2),
        Number(s.total_amount || 0).toFixed(2),
        Number(s.paid_amount || 0).toFixed(2),
        s.status,
      ].join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sales_${scope.replace(/\s+/g, '_')}_${range.from}_to_${range.to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toastSuccess('CSV downloaded');
  };

  // ─── derived summaries ──────────────────────────────────────
  const t = summary?.totals || {};
  const chartData = useMemo(
    () => (summary?.trend || []).map((d) => ({
      day: new Date(d.day).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }),
      revenue: Number(d.revenue) || 0,
      sales:   Number(d.sale_count) || 0,
    })),
    [summary]
  );

  const cashierChartData = useMemo(
    () => (summary?.byCashier || []).slice(0, 8).map((c) => ({
      name: (c.name || 'Unknown').split(' ')[0],
      revenue: Number(c.revenue) || 0,
      count: Number(c.sale_count) || 0,
    })),
    [summary]
  );

  const branchChartData = useMemo(
    () => (summary?.byBranch || []).slice(0, 10).map((b) => ({
      name: (b.name || 'Unknown').length > 12 ? (b.name || 'Unknown').slice(0, 11) + '…' : (b.name || 'Unknown'),
      revenue: Number(b.revenue) || 0,
      count: Number(b.sale_count) || 0,
    })),
    [summary]
  );

  const applyPreset = (key) => { setRangeKey(key); setRange(rangeFor(key)); };
  const setCustomFrom = (v) => { setRangeKey('custom'); setRange((r) => ({ ...r, from: v })); };
  const setCustomTo   = (v) => { setRangeKey('custom'); setRange((r) => ({ ...r, to:   v })); };

  return (
    <>
      {/* ─── Header + range picker + export ─── */}
      <div className="panel">
        <div className="panel-head">
          <div className="head-text">
            <h2>{isCashier ? 'My sales' : 'Sales'}</h2>
            <span className="sub">
              {isCashier
                ? 'Every receipt you rang up — filter by date, review the details, export your own log.'
                : (isSuper
                    ? (branchId
                        ? `Every sale at ${branches.find((b) => Number(b.id) === Number(branchId))?.name || 'this branch'} — filter, analyze, export.`
                        : 'Every sale across every branch. Pick a branch above to drill down, or filter by cashier and date.')
                    : 'All completed sales at this branch. Filter by cashier, date range or payment method — then export.')}
            </span>
          </div>
          <div className="head-actions">
            <button className="btn-ghost" onClick={load} title="Reload">🔄 Refresh</button>
            <button className="btn-primary" onClick={exportCsv} disabled={loading || filteredRows.length === 0}>
              ⬇️ Export CSV
            </button>
          </div>
        </div>

        {/* Date range presets + custom + filters */}
        <div className="sales-filter-bar">
          <div className="segmented">
            {RANGE_PRESETS.map((p) => (
              <button
                key={p.key}
                className={`seg ${rangeKey === p.key ? 'active' : ''}`}
                onClick={() => applyPreset(p.key)}
              >{p.label}</button>
            ))}
            <button className={`seg ${rangeKey === 'custom' ? 'active' : ''}`} onClick={() => setRangeKey('custom')}>
              Custom
            </button>
          </div>

          <div className="sales-filter-dates">
            <input type="date" className="f-input" value={range.from || ''} onChange={(e) => setCustomFrom(e.target.value)} aria-label="From" />
            <span className="muted">to</span>
            <input type="date" className="f-input" value={range.to || ''} onChange={(e) => setCustomTo(e.target.value)} aria-label="To" />
          </div>

          {!isCashier && (
            <select className="f-select" value={cashierId} onChange={(e) => setCashierId(e.target.value)} style={{ minWidth: 180 }}>
              <option value="">All cashiers</option>
              {cashierOptions.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
            </select>
          )}

          <select className="f-select" value={methodKey} onChange={(e) => setMethodKey(e.target.value)} style={{ minWidth: 160 }}>
            <option value="">All payment methods</option>
            {['cash','momo','card','bank','credit'].map((m) => (
              <option key={m} value={m}>{METHOD_LABEL[m] || m}</option>
            ))}
          </select>

          <select className="f-select" value={status} onChange={(e) => setStatus(e.target.value)} style={{ minWidth: 140 }}>
            <option value="">All statuses</option>
            <option value="completed">Completed</option>
            <option value="voided">Voided</option>
            <option value="refunded">Refunded</option>
          </select>
        </div>
      </div>

      {error && <div className="notice" style={{ marginBottom: 14 }}><strong>Couldn't load sales.</strong>&nbsp;{error}</div>}

      {/* ─── Branch picker — super admin only ─── */}
      {isSuper && branches.length > 0 && (
        <div className="ov-branches-chips" style={{ paddingBottom: 14 }}>
          <button
            className={`ov-chip combined ${!branchId ? 'active' : ''}`}
            onClick={() => setBranchId('')}
            title="Every sale from every branch"
          >
            <span className="ic">🌐</span>
            All branches
            <span className="hq-tag" style={{ background: 'var(--accent)' }}>COMBINED</span>
          </button>
          {branches.map((b) => (
            <button
              key={b.id}
              className={`ov-chip ${Number(branchId) === b.id ? 'active' : ''}`}
              onClick={() => setBranchId(String(b.id))}
              title={`Only sales at ${b.name}`}
            >
              <span className="ic">{b.is_head_office ? '🏛️' : '🏬'}</span>
              {b.name}
              {b.is_head_office ? <span className="hq-tag">HQ</span> : null}
            </button>
          ))}
        </div>
      )}

      {/* ─── KPI strip ─── */}
      <div className="kpi-row">
        <KpiStat
          label="Revenue" val={fmtMoney(t.revenue || 0)} raw
          hint={`${fmtNumber(t.sale_count || 0)} sales`}
          tone="brand" icon="💰"
        />
        <KpiStat
          label="Average ticket" val={fmtMoney(t.avg_ticket || 0)} raw
          hint={t.sale_count > 0 ? `over ${fmtNumber(t.sale_count)} receipts` : 'no sales yet'}
          tone="accent" icon="🎫"
        />
        <KpiStat
          label="Discount given" val={fmtMoney(t.discount || 0)} raw
          hint={t.revenue > 0 ? `${((t.discount / t.revenue) * 100).toFixed(1)}% of revenue` : '—'}
          tone="warn" icon="🎁"
        />
        <KpiStat
          label={isCashier ? 'Your first sale' : 'Cashiers'}
          val={isCashier
            ? (t.first_at ? new Date(t.first_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : '—')
            : fmtNumber((summary?.byCashier || []).length)}
          hint={isCashier
            ? (t.last_at ? `last at ${new Date(t.last_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}` : '')
            : 'active in this period'}
          tone="success" icon={isCashier ? '⏱️' : '👤'}
        />
      </div>

      {/* ─── Trend chart + payment method mix ─── */}
      <div className="sales-charts">
        <div className="panel" style={{ margin: 0 }}>
          <div className="panel-head">
            <div className="head-text">
              <h2 style={{ fontSize: 15 }}>Sales trend</h2>
              <span className="sub">Revenue per day in the selected range</span>
            </div>
          </div>
          {chartData.length === 0 ? (
            <div className="empty-state" style={{ padding: 30 }}>
              <div className="ic">📈</div>
              <h4>No sales yet in this range</h4>
              <p>Adjust the date filter or record a sale from the POS.</p>
            </div>
          ) : (
            <div style={{ width: '100%', height: 240 }}>
              <ResponsiveContainer>
                <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="revGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%"   stopColor="var(--brand)" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="var(--brand)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
                  <XAxis dataKey="day" tick={{ fill: 'var(--mute)', fontSize: 11 }} />
                  <YAxis tick={{ fill: 'var(--mute)', fontSize: 11 }} tickFormatter={(v) => (v >= 1000 ? `${(v/1000).toFixed(1)}k` : v)} />
                  <Tooltip
                    contentStyle={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 8, color: 'var(--ink)' }}
                    formatter={(v, k) => k === 'revenue' ? [fmtMoney(v), 'Revenue'] : [v, 'Sales']}
                  />
                  <Area type="monotone" dataKey="revenue" stroke="var(--brand)" strokeWidth={2} fill="url(#revGradient)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="panel" style={{ margin: 0 }}>
          <div className="panel-head">
            <div className="head-text">
              <h2 style={{ fontSize: 15 }}>Payment mix</h2>
              <span className="sub">How customers paid</span>
            </div>
          </div>
          {(summary?.byMethod || []).length === 0 ? (
            <div className="empty-state" style={{ padding: 30 }}>
              <div className="ic">💳</div>
              <h4>No payments yet</h4>
            </div>
          ) : (
            <div className="pm-list">
              {(summary.byMethod).map((m) => {
                const total = (summary.byMethod).reduce((s, x) => s + Number(x.amount), 0) || 1;
                const pct = (Number(m.amount) / total) * 100;
                return (
                  <div key={m.method} className="pm-row">
                    <div className="pm-row-head">
                      <span>{METHOD_LABEL[m.method] || titleCase(m.method)}</span>
                      <strong>{fmtMoney(m.amount)}</strong>
                    </div>
                    <div className="pm-bar"><div className="pm-fill" style={{ width: `${pct}%` }} /></div>
                    <div className="pm-row-foot">
                      <span className="muted">{fmtNumber(m.sale_count)} sales</span>
                      <span className="muted">{pct.toFixed(1)}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ─── Branch leaderboard — super admin, all-branches view ─── */}
      {isSuper && !branchId && (summary?.byBranch || []).length > 1 && (
        <div className="panel">
          <div className="panel-head">
            <div className="head-text">
              <h2 style={{ fontSize: 15 }}>Sales by branch</h2>
              <span className="sub">Click a branch to filter every panel below to just that one.</span>
            </div>
            {branchId && (
              <button className="btn-ghost btn-sm" onClick={() => setBranchId('')}>Clear filter ×</button>
            )}
          </div>

          <div className="cashier-grid">
            {summary.byBranch.map((b, i) => (
              <button
                key={b.branch_id || i}
                className={`cashier-card ${String(branchId) === String(b.branch_id) ? 'active' : ''}`}
                onClick={() => setBranchId(branchId === String(b.branch_id) ? '' : String(b.branch_id))}
              >
                <div className="ch-rank">{b.is_head_office ? '🏛️' : i + 1}</div>
                <div className="ch-body">
                  <div className="ch-name">
                    {b.name || 'Unknown'}
                    {b.is_head_office ? <span className="hq-tag" style={{ marginLeft: 6, background: 'var(--accent)', color: '#fff', fontSize: 9, padding: '1px 5px', borderRadius: 4 }}>HQ</span> : null}
                  </div>
                  <div className="ch-nums">
                    <div><span className="lbl">Sales</span><span className="val">{fmtNumber(b.sale_count)}</span></div>
                    <div><span className="lbl">Revenue</span><span className="val brand">{fmtMoney(b.revenue)}</span></div>
                    <div><span className="lbl">Avg</span><span className="val">{fmtMoney(b.avg_ticket)}</span></div>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {branchChartData.length > 1 && (
            <div style={{ width: '100%', height: 200, marginTop: 14 }}>
              <ResponsiveContainer>
                <BarChart data={branchChartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
                  <XAxis dataKey="name" tick={{ fill: 'var(--mute)', fontSize: 11 }} />
                  <YAxis tick={{ fill: 'var(--mute)', fontSize: 11 }} tickFormatter={(v) => (v >= 1000 ? `${(v/1000).toFixed(1)}k` : v)} />
                  <Tooltip
                    contentStyle={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 8, color: 'var(--ink)' }}
                    formatter={(v) => [fmtMoney(v), 'Revenue']}
                  />
                  <Bar dataKey="revenue" fill="var(--accent)" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* ─── Cashier leaderboard — manager+ only ─── */}
      {isManager && (summary?.byCashier || []).length > 0 && (
        <div className="panel">
          <div className="panel-head">
            <div className="head-text">
              <h2 style={{ fontSize: 15 }}>Cashier performance</h2>
              <span className="sub">Click a name to filter the table below to just their sales.</span>
            </div>
            {cashierId && (
              <button className="btn-ghost btn-sm" onClick={() => setCashierId('')}>Clear filter ×</button>
            )}
          </div>

          <div className="cashier-grid">
            {summary.byCashier.slice(0, 12).map((c, i) => (
              <button
                key={c.user_id || i}
                className={`cashier-card ${String(cashierId) === String(c.user_id) ? 'active' : ''}`}
                onClick={() => setCashierId(cashierId === String(c.user_id) ? '' : String(c.user_id))}
              >
                <div className="ch-rank">{i + 1}</div>
                <div className="ch-body">
                  <div className="ch-name">{c.name || 'Unknown'}</div>
                  <div className="ch-nums">
                    <div><span className="lbl">Sales</span><span className="val">{fmtNumber(c.sale_count)}</span></div>
                    <div><span className="lbl">Revenue</span><span className="val brand">{fmtMoney(c.revenue)}</span></div>
                    <div><span className="lbl">Avg</span><span className="val">{fmtMoney(c.avg_ticket)}</span></div>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {cashierChartData.length > 1 && (
            <div style={{ width: '100%', height: 180, marginTop: 12 }}>
              <ResponsiveContainer>
                <BarChart data={cashierChartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
                  <XAxis dataKey="name" tick={{ fill: 'var(--mute)', fontSize: 11 }} />
                  <YAxis tick={{ fill: 'var(--mute)', fontSize: 11 }} tickFormatter={(v) => (v >= 1000 ? `${(v/1000).toFixed(1)}k` : v)} />
                  <Tooltip
                    contentStyle={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 8, color: 'var(--ink)' }}
                    formatter={(v) => [fmtMoney(v), 'Revenue']}
                  />
                  <Bar dataKey="revenue" fill="var(--brand)" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* ─── Sales table ─── */}
      <div className="panel">
        <div className="panel-head">
          <div className="head-text">
            <h2 style={{ fontSize: 15 }}>
              Receipts
              {cashierId && (() => {
                const name = users.find((u) => u.id === Number(cashierId))?.full_name;
                return name ? <span className="muted" style={{ fontWeight: 500, marginLeft: 8 }}>— filtered to {name}</span> : null;
              })()}
            </h2>
            <span className="sub">{filteredRows.length} of {rows.length} rows visible</span>
          </div>
          <div className="head-actions">
            <div className="search-field" style={{ maxWidth: 300 }}>
              <span className="ic">🔍</span>
              <input
                className="f-input"
                placeholder="Search receipt, cashier, customer…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && <button className="clear" onClick={() => setSearch('')} aria-label="Clear">×</button>}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="empty-state"><div className="ic">⏳</div><h4>Loading sales…</h4></div>
        ) : filteredRows.length === 0 ? (
          <div className="empty-state">
            <div className="ic">🧾</div>
            <h4>No sales match these filters.</h4>
            <p>Try widening the date range or clearing filters.</p>
          </div>
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Receipt</th>
                  <th>Date</th>
                  <th>Cashier</th>
                  <th>Customer</th>
                  <th className="right">Total</th>
                  <th className="right">Paid</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((s) => (
                  <tr key={s.id} style={{ cursor: 'pointer' }} onClick={() => openSale(s.id)}>
                    <td className="strong">
                      {s.receipt_no}
                      {isSuper && s.branch_name && <div className="muted" style={{ fontSize: 12 }}>{s.branch_name}</div>}
                    </td>
                    <td className="muted">{fmtDate(s.created_at)}</td>
                    <td>{s.cashier_name || '—'}</td>
                    <td>{s.customer_name || <span className="muted">Walk-in</span>}</td>
                    <td className="right num strong">{fmtMoney(s.total_amount)}</td>
                    <td className="right num">{fmtMoney(s.paid_amount)}</td>
                    <td>
                      <span className={`badge ${s.status === 'completed' ? 'badge-green' : s.status === 'voided' ? 'badge-red' : 'badge-gray'}`}>
                        {titleCase(s.status)}
                      </span>
                    </td>
                    <td className="right">
                      <button className="btn-link" onClick={(e) => { e.stopPropagation(); openSale(s.id); }}>View →</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ─── Sale detail modal (existing Receipt) ─── */}
      <Modal
        title={view ? `Receipt — ${view.sale.receipt_no}` : 'Receipt'}
        open={!!view} onClose={() => setView(null)} width={420}
        footer={
          <>
            <button className="btn-ghost" onClick={() => setView(null)}>Close</button>
            <button className="btn-primary" onClick={() => window.print()}>🖨 Print</button>
          </>
        }
      >
        {view && (
          <Receipt
            sale={view.sale} items={view.items} payments={view.payments}
            facility={facility} branchName={view.branchName} cashier={view.cashier}
          />
        )}
      </Modal>
    </>
  );
};

// ─── KPI stat card ─────────────────────────────────────────────
const KpiStat = ({ label, val, hint, tone, icon, raw }) => (
  <div className="stat">
    <div className="lbl">{label}</div>
    <div className="val">{raw ? val : val}</div>
    <div className="hint">{hint}</div>
    <div className="ic" style={
      tone === 'brand'   ? { background: 'var(--brand-soft)',   color: 'var(--brand)' }
    : tone === 'accent'  ? { background: 'var(--accent-soft)',  color: 'var(--accent)' }
    : tone === 'warn'    ? { background: 'var(--warn-soft)',    color: 'var(--warn)' }
    : tone === 'success' ? { background: 'var(--success-soft)', color: 'var(--success)' }
    : {}}>{icon}</div>
  </div>
);

export default Sales;
